import React, { useMemo, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import EngagementIcon from 'assets/icons/EngagementIcon'
import ReachIcon from 'assets/icons/ReachIcon'
import ViewsIcon from 'assets/icons/ViewsIcon'
import { genders } from 'constants/campaign'
import { SocialAccount, SocialAccountDemographicInsights } from 'types/influencer'
import { formatNumber, isEmpty } from 'utils/helpers'
import { isNumber } from 'lodash'
import '../style.scss'

const TOP_LOCATION_LIMIT = 10
const AGE_BUCKET_LIMIT = 100
const DONUT_COLORS = ['#0069ad', '#5ba3cf', '#b2d2e6', '#8ec5e8', '#d4e8f5']

interface SocialAccountInsightsProps {
    socialAccount: SocialAccount
    separatePanel?: boolean
}

interface DemographicEntry {
    key: string
    label: string
    value: number
}

const genderLabel = (key: string): string => {
    const normalized = key.trim().length === 1 ? key.trim().toUpperCase() : key.trim()
    return (genders as Record<string, string>)[normalized] ?? key
}

const ageSortKey = (label: string): number => {
    const normalized = label.trim()
    // Examples: "13-17", "18 - 24", "65+"
    const plusMatch = normalized.match(/^(\d+)\s*\+$/)
    if (plusMatch?.[1]) return Number(plusMatch[1])

    const rangeMatch = normalized.match(/^(\d+)\s*-\s*(\d+)$/)
    if (rangeMatch?.[1]) return Number(rangeMatch[1])

    const firstNumber = normalized.match(/(\d+)/)?.[1]
    if (firstNumber) return Number(firstNumber)

    return Number.POSITIVE_INFINITY
}

const getTopDemographicEntries = (
    data?: Record<string, number>,
    limit = TOP_LOCATION_LIMIT,
    labelFn: (key: string) => string = (key) => key
): DemographicEntry[] => {
    if (!data) return []

    return Object.entries(data)
        .filter(([, value]) => typeof value === 'number' && Number.isFinite(value) && value > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([key, value]) => ({
            key,
            label: labelFn(key),
            value,
        }))
}

const getAgeDemographicEntries = (data?: Record<string, number>): DemographicEntry[] => {
    if (!data) return []

    return Object.entries(data)
        .filter(([, value]) => typeof value === 'number' && Number.isFinite(value) && value > 0)
        .map(([key, value]) => ({
            key,
            label: key,
            value,
        }))
        .sort((a, b) => ageSortKey(a.label) - ageSortKey(b.label))
        .slice(0, AGE_BUCKET_LIMIT)
}

const formatMetricValue = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null
    if (isNumber(value)) return formatNumber(value)
    const parsed = Number(value)
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) return formatNumber(parsed)
    return String(value)
}

const formatEngagementRate = (rate?: string): string | null => {
    if (!rate) return null
    const trimmed = rate.trim()
    return trimmed.includes('%') ? trimmed : `${trimmed}%`
}

const DemographicDonutCard = ({
    title,
    entries,
    legendEntries,
    onLegendToggle,
    legendToggleLabel,
}: {
    title: string
    entries: DemographicEntry[]
    legendEntries?: DemographicEntry[]
    onLegendToggle?: () => void
    legendToggleLabel?: string
}) => {
    const colorByKey = useMemo(() => {
        const map: Record<string, string> = {}
        entries.forEach((entry, index) => {
            map[entry.key] = DONUT_COLORS[index % DONUT_COLORS.length]
        })
        return map
    }, [entries])

    const chartData = entries.map((entry) => ({
        name: entry.label,
        value: entry.value,
        color: colorByKey[entry.key] ?? DONUT_COLORS[0],
    }))

    if (chartData.length === 0) return null

    const legendList = legendEntries ?? entries

    return (
        <div className="social-account-insights__donut-card">
            <p className="social-account-insights__donut-title">{title}</p>
            <div className="social-account-insights__donut-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius="62%"
                            outerRadius="100%"
                            paddingAngle={1}
                            stroke="none"
                            isAnimationActive={false}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`${entry.name}-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip
                            content={({ active, payload }) => {
                                if (!active || !payload || payload.length === 0) return null
                                const item: any = payload[0]
                                const label = String(item?.name ?? item?.payload?.name ?? '')
                                const value = item?.value
                                if (!label) return null

                                return (
                                    <div
                                        style={{
                                            padding: '10px 12px',
                                            borderRadius: 10,
                                            border: '1px solid #e5e5e5',
                                            background: '#fff',
                                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                                            fontFamily: 'Rubik, sans-serif',
                                            fontSize: 12,
                                            color: '#111',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {label}: {value}%
                                    </div>
                                )
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <ul className="social-account-insights__donut-legend">
                {legendList.map((entry) => (
                    <li key={entry.key} className="social-account-insights__donut-legend-row">
                        <span className="social-account-insights__donut-legend-label">
                            <span
                                className="social-account-insights__donut-dot"
                                style={{ backgroundColor: colorByKey[entry.key] ?? DONUT_COLORS[0] }}
                                aria-hidden
                            />
                            {entry.label}
                        </span>
                        <span className="social-account-insights__donut-pct">{entry.value}%</span>
                    </li>
                ))}
                {onLegendToggle && legendToggleLabel ? (
                    <li className="social-account-insights__donut-legend-row">
                        <button
                            type="button"
                            className="social-account-insights__donut-toggle"
                            onClick={onLegendToggle}
                        >
                            {legendToggleLabel}
                        </button>
                    </li>
                ) : null}
            </ul>
        </div>
    )
}

const LocationBarsCard = ({
    title,
    entries,
}: {
    title: string
    entries: DemographicEntry[]
}) => {
    if (entries.length === 0) return null

    return (
        <div className="social-account-insights__location-card">
            <p className="social-account-insights__location-title">{title}</p>
            <div className="social-account-insights__location-list">
                {entries.map((entry) => (
                    <div key={entry.key} className="social-account-insights__bar-group">
                        <div className="social-account-insights__bar-header">
                            <span className="social-account-insights__bar-label">{entry.label}</span>
                            <span className="social-account-insights__bar-value">{entry.value}%</span>
                        </div>
                        <div className="social-account-insights__bar-track">
                            <div
                                className="social-account-insights__bar-fill"
                                style={{ width: `${entry.value}%` }}
                                role="progressbar"
                                aria-valuenow={entry.value}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const SocialAccountInsights: React.FC<SocialAccountInsightsProps> = ({
    socialAccount,
    separatePanel = false,
}) => {
    const [isAgeExpanded, setIsAgeExpanded] = useState(false)
    const platform = socialAccount.platform
    const averages = platform === 'ig' ? socialAccount.ig?.averages : socialAccount.yt?.averages
    const medians = platform === 'ig' ? socialAccount.ig?.medians : socialAccount.yt?.medians
    const demographic = useMemo((): SocialAccountDemographicInsights => {
        return socialAccount.ig?.insights?.demographic ?? {}
    }, [socialAccount.ig?.insights?.demographic])

    const metricRows = useMemo(() => {
        const avgEngagement =
            (isNumber(averages?.likes) && isNumber(averages?.comments)
                ? formatNumber(Number(averages.likes) + Number(averages.comments))
                : null)

        return [
            { label: 'Avg Reach', value: formatMetricValue(averages?.reach), icon: ReachIcon },
            {
                label: 'Median Views',
                value: formatMetricValue(medians?.views),
                icon: ViewsIcon,
            },
            { label: 'Avg Engagement', value: avgEngagement, icon: 'heart' as const },
            {
                label: 'Avg ER',
                value: formatEngagementRate(socialAccount.engagement_rate),
                icon: EngagementIcon,
            },
        ].filter((row) => row.value !== null)
    }, [averages, socialAccount.engagement_rate])

    const genderEntries = useMemo(
        () => getTopDemographicEntries(demographic.gender, TOP_LOCATION_LIMIT, genderLabel),
        [demographic.gender]
    )

    const ageEntries = useMemo(
        () => getAgeDemographicEntries(demographic.age),
        [demographic.age]
    )

    const ageLegendEntries = useMemo(() => {
        if (isAgeExpanded) return ageEntries
        return ageEntries.slice(0, 2)
    }, [ageEntries, isAgeExpanded])

    const showAgeToggle = ageEntries.length > 2
    const ageToggleLabel = isAgeExpanded ? 'Show less' : 'Show all'

    const cityEntries = useMemo(
        () => getTopDemographicEntries(demographic.city, TOP_LOCATION_LIMIT),
        [demographic.city]
    )

    const countryEntries = useMemo(
        () => getTopDemographicEntries(demographic.country, TOP_LOCATION_LIMIT),
        [demographic.country]
    )

    const hasAudienceSection =
        genderEntries.length > 0 ||
        ageEntries.length > 0 ||
        cityEntries.length > 0 ||
        countryEntries.length > 0

    const hasDemographicData =
        !isEmpty(demographic) &&
        (Object.keys(demographic.gender ?? {}).length > 0 ||
            Object.keys(demographic.age ?? {}).length > 0 ||
            Object.keys(demographic.city ?? {}).length > 0 ||
            Object.keys(demographic.country ?? {}).length > 0)

    // if (metricRows.length === 0 && !hasDemographicData) {
    if (!hasDemographicData) {
        return null
    }

    return (
        <div className="social-account-insights">
            {
                separatePanel &&
                <h2 className="social-account-insights__heading">Insights</h2>
            }
            {/* {metricRows.length > 0 && (
                <div className="social-account-insights__metrics-card">
                    {metricRows.map((row) => {
                        const Icon = typeof row.icon === 'string' ? null : row.icon
                        return (
                            <div key={row.label} className="social-account-insights__metric-row">
                                <span className="social-account-insights__metric-label">
                                    {Icon ? (
                                        <Icon width={16} height={16} />
                                    ) : (
                                        <i className="mdi mdi-heart-outline social-account-insights__metric-heart" aria-hidden />
                                    )}
                                    {row.label}
                                </span>
                                <span className="social-account-insights__metric-value">{row.value}</span>
                            </div>
                        )
                    })}
                </div>
            )} */}

            {hasAudienceSection && (
                <section className="social-account-insights__audience">
                    <h3 className="social-account-insights__audience-heading">Audience</h3>
                    {(genderEntries.length > 0 || ageEntries.length > 0) && (
                        <div className="social-account-insights__donut-grid">
                            <DemographicDonutCard title="GENDER SPLIT" entries={genderEntries} />
                            <DemographicDonutCard
                                title="AGE SPLIT"
                                entries={ageEntries}
                                legendEntries={ageLegendEntries}
                                onLegendToggle={
                                    showAgeToggle ? () => setIsAgeExpanded((prev) => !prev) : undefined
                                }
                                legendToggleLabel={showAgeToggle ? ageToggleLabel : undefined}
                            />
                        </div>
                    )}
                    <LocationBarsCard title="TOP TOWN/CITIES" entries={cityEntries} />
                    <LocationBarsCard title="TOP COUNTRY" entries={countryEntries} />
                </section>
            )}
        </div>
    )
}

export default SocialAccountInsights
